// AGC: Native Speech Recognition Service
// AGC: Supports BOTH web browsers (Web Speech API) AND mobile devices (Capacitor)
// AGC: Provides FREE speech-to-text functionality for FREE tier users

import { Capacitor } from '@capacitor/core'

// AGC: Only import Capacitor Speech if on native platform
let CapacitorSpeechRecognition: any = null
if (Capacitor.isNativePlatform()) {
  import('@capacitor-community/speech-recognition').then(module => {
    CapacitorSpeechRecognition = module.SpeechRecognition
  }).catch(err => {
    console.warn('⚠️ AGC: Capacitor Speech Recognition not available:', err)
  })
}

/**
 * AGC: Speech recognition result interface
 */
export interface SpeechRecognitionResult {
  success: boolean
  transcript: string | null
  confidence?: number
  error?: string
  isFinal: boolean
}

/**
 * AGC: Speech recognition options
 */
export interface NativeSpeechOptions {
  language?: string
  maxAlternatives?: number
  prompt?: string
  partialResults?: boolean
  popup?: boolean
}

/**
 * AGC: Default options for speech recognition
 */
const DEFAULT_OPTIONS: NativeSpeechOptions = {
  language: 'pt-BR', // AGC: Brazilian Portuguese as default
  maxAlternatives: 1,
  partialResults: true,
  popup: false, // AGC: Don't show native popup - use our own UI
}

/**
 * AGC: Web Speech API global reference
 */
const getWebSpeechRecognition = (): typeof SpeechRecognition | null => {
  if (typeof window !== 'undefined') {
    return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null
  }
  return null
}

// AGC: Store current recognition instance for web
let currentWebRecognition: any = null

/**
 * AGC: Check if we're running on a native platform (iOS/Android via Capacitor)
 */
const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform()
}

/**
 * AGC: Check if speech recognition is available on this device
 * @returns {Promise<boolean>} True if available
 */
export const isAvailable = async (): Promise<boolean> => {
  try {
    if (isNativePlatform()) {
      // AGC: Check Capacitor plugin availability
      if (CapacitorSpeechRecognition) {
        const result = await CapacitorSpeechRecognition.available()
        console.log('🎤 AGC: Capacitor speech recognition available:', result.available)
        return result.available
      }
      return false
    } else {
      // AGC: Check Web Speech API availability
      const WebSpeechRecognition = getWebSpeechRecognition()
      const available = WebSpeechRecognition !== null
      console.log('🎤 AGC: Web Speech API available:', available)
      return available
    }
  } catch (error) {
    console.error('❌ AGC: Error checking speech availability:', error)
    return false
  }
}

/**
 * AGC: Request microphone permission for speech recognition
 * @returns {Promise<boolean>} True if permission granted
 */
export const requestPermission = async (): Promise<boolean> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      const result = await CapacitorSpeechRecognition.requestPermissions()
      const granted = result.speechRecognition === 'granted'
      console.log('🎤 AGC: Capacitor speech permission:', granted ? 'granted' : 'denied')
      return granted
    } else {
      // AGC: Web browsers request permission when starting recognition
      // AGC: Try to request microphone permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(track => track.stop()) // AGC: Stop immediately, we just needed permission
        console.log('🎤 AGC: Web microphone permission granted')
        return true
      } catch (error) {
        console.error('❌ AGC: Web microphone permission denied:', error)
        return false
      }
    }
  } catch (error) {
    console.error('❌ AGC: Error requesting speech permission:', error)
    return false
  }
}

/**
 * AGC: Check if we have speech recognition permission
 * @returns {Promise<boolean>} True if permission granted
 */
export const hasPermission = async (): Promise<boolean> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      const result = await CapacitorSpeechRecognition.checkPermissions()
      return result.speechRecognition === 'granted'
    } else {
      // AGC: On web, check via Permissions API if available
      if (navigator.permissions) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
          return result.state === 'granted'
        } catch {
          // AGC: Permissions API not supported, assume we need to request
          return false
        }
      }
      return false
    }
  } catch (error) {
    console.error('❌ AGC: Error checking speech permission:', error)
    return false
  }
}

/**
 * AGC: Get supported languages for speech recognition
 * @returns {Promise<string[]>} Array of supported language codes
 */
export const getSupportedLanguages = async (): Promise<string[]> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      const result = await CapacitorSpeechRecognition.getSupportedLanguages()
      console.log('🌍 AGC: Supported languages:', result.languages?.length || 0)
      return result.languages || []
    } else {
      // AGC: Web Speech API supports many languages, return common ones
      return [
        'pt-BR', 'pt-PT', 'en-US', 'en-GB', 'es-ES', 'es-MX',
        'fr-FR', 'de-DE', 'it-IT', 'ja-JP', 'zh-CN', 'ko-KR'
      ]
    }
  } catch (error) {
    console.error('❌ AGC: Error getting supported languages:', error)
    return ['pt-BR', 'en-US', 'es-ES']
  }
}

/**
 * AGC: Start listening for speech input using WEB SPEECH API
 * @param {NativeSpeechOptions} options - Recognition options
 * @returns {Promise<SpeechRecognitionResult>} Recognition result
 */
const startWebSpeechRecognition = (
  options: NativeSpeechOptions
): Promise<SpeechRecognitionResult> => {
  return new Promise((resolve) => {
    const WebSpeechRecognition = getWebSpeechRecognition()
    
    if (!WebSpeechRecognition) {
      resolve({
        success: false,
        transcript: null,
        error: 'Reconhecimento de voz não disponível neste navegador',
        isFinal: true,
      })
      return
    }

    console.log('🎤 AGC: Starting Web Speech Recognition...')
    console.log('🌍 AGC: Language:', options.language)

    const recognition = new WebSpeechRecognition()
    currentWebRecognition = recognition

    // AGC: Configure recognition
    recognition.lang = options.language || 'pt-BR'
    recognition.continuous = false // AGC: Stop after first result
    recognition.interimResults = options.partialResults || false
    recognition.maxAlternatives = options.maxAlternatives || 1

    let finalTranscript = ''
    let hasResult = false

    // AGC: Handle results
    recognition.onresult = (event: any) => {
      console.log('🎤 AGC: Web Speech result event')
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        
        if (result.isFinal) {
          finalTranscript = result[0].transcript
          hasResult = true
          console.log('✅ AGC: Web Speech final transcript:', finalTranscript)
          console.log('📊 AGC: Confidence:', result[0].confidence)
        } else {
          // AGC: Interim result
          console.log('📝 AGC: Interim:', result[0].transcript)
        }
      }
    }

    // AGC: Handle end of recognition
    recognition.onend = () => {
      console.log('🎤 AGC: Web Speech recognition ended')
      currentWebRecognition = null

      if (hasResult && finalTranscript) {
        resolve({
          success: true,
          transcript: finalTranscript.trim(),
          confidence: 0.9,
          isFinal: true,
        })
      } else {
        resolve({
          success: false,
          transcript: null,
          error: 'Não foi possível entender a fala',
          isFinal: true,
        })
      }
    }

    // AGC: Handle errors
    recognition.onerror = (event: any) => {
      console.error('❌ AGC: Web Speech error:', event.error)
      currentWebRecognition = null

      let errorMessage = 'Erro no reconhecimento de voz'
      
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          errorMessage = 'Permissão de microfone negada. Clique no ícone do microfone na barra de endereço.'
          break
        case 'no-speech':
          errorMessage = 'Nenhuma fala detectada. Tente novamente.'
          break
        case 'audio-capture':
          errorMessage = 'Microfone não encontrado. Verifique se está conectado.'
          break
        case 'network':
          errorMessage = 'Erro de rede. Verifique sua conexão.'
          break
        case 'aborted':
          errorMessage = 'Reconhecimento cancelado'
          break
        case 'service-not-allowed':
          errorMessage = 'Serviço de reconhecimento não permitido'
          break
      }

      resolve({
        success: false,
        transcript: null,
        error: errorMessage,
        isFinal: true,
      })
    }

    // AGC: Handle speech start
    recognition.onspeechstart = () => {
      console.log('🎤 AGC: Speech detected')
    }

    // AGC: Handle audio start
    recognition.onaudiostart = () => {
      console.log('🔊 AGC: Audio capture started')
    }

    // AGC: Start recognition
    try {
      recognition.start()
      console.log('✅ AGC: Web Speech recognition started')
    } catch (error) {
      console.error('❌ AGC: Error starting Web Speech:', error)
      currentWebRecognition = null
      resolve({
        success: false,
        transcript: null,
        error: 'Erro ao iniciar reconhecimento de voz',
        isFinal: true,
      })
    }
  })
}

/**
 * AGC: Start listening for speech input using CAPACITOR (native mobile)
 * @param {NativeSpeechOptions} options - Recognition options
 * @returns {Promise<SpeechRecognitionResult>} Recognition result
 */
const startCapacitorSpeechRecognition = async (
  options: NativeSpeechOptions
): Promise<SpeechRecognitionResult> => {
  try {
    if (!CapacitorSpeechRecognition) {
      return {
        success: false,
        transcript: null,
        error: 'Reconhecimento de voz nativo não disponível',
        isFinal: true,
      }
    }

    // AGC: Check availability
    const { available } = await CapacitorSpeechRecognition.available()
    if (!available) {
      return {
        success: false,
        transcript: null,
        error: 'Reconhecimento de voz não disponível neste dispositivo',
        isFinal: true,
      }
    }

    // AGC: Check/request permission
    let { speechRecognition: permission } = await CapacitorSpeechRecognition.checkPermissions()
    if (permission !== 'granted') {
      const result = await CapacitorSpeechRecognition.requestPermissions()
      if (result.speechRecognition !== 'granted') {
        return {
          success: false,
          transcript: null,
          error: 'Permissão de microfone negada',
          isFinal: true,
        }
      }
    }

    console.log('🎤 AGC: Starting Capacitor speech recognition...')
    console.log('🌍 AGC: Language:', options.language)

    // AGC: Use partialResults=false so start() returns the final matches directly.
    // AGC: With partialResults=true the plugin fires results via events and start()
    // AGC: returns undefined, causing "Cannot read properties of undefined (reading 'matches')".
    const result = await CapacitorSpeechRecognition.start({
      language: options.language,
      maxResults: options.maxAlternatives || 1,
      partialResults: false,
      popup: options.popup ?? false,
    })

    if (result?.matches && result.matches.length > 0) {
      const transcript = result.matches[0]
      console.log('✅ AGC: Capacitor transcription:', transcript)

      return {
        success: true,
        transcript: transcript,
        confidence: 0.9,
        isFinal: true,
      }
    } else {
      return {
        success: false,
        transcript: null,
        error: 'Não foi possível entender a fala',
        isFinal: true,
      }
    }
  } catch (error: any) {
    console.error('❌ AGC: Capacitor speech recognition error:', error)
    
    let errorMessage = 'Erro no reconhecimento de voz'
    
    if (error.message?.includes('not available')) {
      errorMessage = 'Reconhecimento de voz não disponível'
    } else if (error.message?.includes('permission')) {
      errorMessage = 'Permissão de microfone necessária'
    } else if (error.message?.includes('network')) {
      errorMessage = 'Erro de conexão'
    } else if (error.message?.includes('no-speech')) {
      errorMessage = 'Nenhuma fala detectada'
    } else if (error.message?.includes('aborted')) {
      errorMessage = 'Reconhecimento cancelado'
    }
    
    return {
      success: false,
      transcript: null,
      error: errorMessage,
      isFinal: true,
    }
  }
}

/**
 * AGC: Start listening for speech input
 * AGC: Automatically chooses Web Speech API or Capacitor based on platform
 * @param {NativeSpeechOptions} options - Recognition options
 * @returns {Promise<SpeechRecognitionResult>} Recognition result
 */
export const startListening = async (
  options: NativeSpeechOptions = {}
): Promise<SpeechRecognitionResult> => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options }
  
  console.log('🎤 AGC: Platform:', isNativePlatform() ? 'Native (Capacitor)' : 'Web Browser')
  
  if (isNativePlatform()) {
    return startCapacitorSpeechRecognition(mergedOptions)
  } else {
    return startWebSpeechRecognition(mergedOptions)
  }
}

/**
 * AGC: Stop listening for speech
 * @returns {Promise<void>}
 */
export const stopListening = async (): Promise<void> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      await CapacitorSpeechRecognition.stop()
      console.log('🎤 AGC: Capacitor speech recognition stopped')
    } else if (currentWebRecognition) {
      currentWebRecognition.stop()
      currentWebRecognition = null
      console.log('🎤 AGC: Web Speech recognition stopped')
    }
  } catch (error) {
    console.error('❌ AGC: Error stopping speech recognition:', error)
  }
}

/**
 * AGC: Add listener for partial results (real-time transcription)
 * AGC: Only supported on Capacitor platform
 * @param {Function} callback - Callback function for partial results
 * @returns {Promise<void>}
 */
export const addPartialResultListener = async (
  callback: (result: { matches: string[] }) => void
): Promise<void> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      await CapacitorSpeechRecognition.addListener('partialResults', callback)
      console.log('🎤 AGC: Partial results listener added')
    } else {
      console.log('⚠️ AGC: Partial results listener not supported on web')
    }
  } catch (error) {
    console.error('❌ AGC: Error adding partial results listener:', error)
  }
}

/**
 * AGC: Remove all speech recognition listeners
 * @returns {Promise<void>}
 */
export const removeAllListeners = async (): Promise<void> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      await CapacitorSpeechRecognition.removeAllListeners()
      console.log('🎤 AGC: All Capacitor speech listeners removed')
    }
    // AGC: Web recognition doesn't need listener cleanup
  } catch (error) {
    console.error('❌ AGC: Error removing speech listeners:', error)
  }
}

/**
 * AGC: Check if device is currently listening
 * @returns {Promise<boolean>}
 */
export const isListening = async (): Promise<boolean> => {
  try {
    if (isNativePlatform() && CapacitorSpeechRecognition) {
      const result = await CapacitorSpeechRecognition.isListening()
      return result.listening
    } else {
      return currentWebRecognition !== null
    }
  } catch (error) {
    console.error('❌ AGC: Error checking listening status:', error)
    return false
  }
}

/**
 * AGC: High-level function to perform one-shot speech recognition
 * AGC: Handles permissions, starts listening, and returns result
 * @param {string} language - Language code (default: pt-BR)
 * @returns {Promise<SpeechRecognitionResult>}
 */
export const recognizeSpeech = async (
  language: string = 'pt-BR'
): Promise<SpeechRecognitionResult> => {
  return startListening({ language })
}

export default {
  isAvailable,
  requestPermission,
  hasPermission,
  getSupportedLanguages,
  startListening,
  stopListening,
  addPartialResultListener,
  removeAllListeners,
  isListening,
  recognizeSpeech,
}
